import { Injectable } from '@angular/core';
import {environment} from '../../../environments/environment';
import { catchError, map, retry } from 'rxjs/operators';
import {HttpErrorHandler} from '../../http.error.handler';
import { HttpClient, HttpParams } from '@angular/common/http';
import {ImageModel, ImageWithDriverAndVehicle} from '../../shared/models/image';
import {Page} from '../../shared/models/Page';
import {JsonApiResponse} from '../../shared/models/JsonApiResponse';
import {Moment} from 'moment';
import {MultiSelectFilter} from '../../shared/components/multi-select-component';
import { firstValueFrom } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class ImageService {

  constructor(private http: HttpClient) { }

  getImageByTime(vehicleId: number, shiftId: number, time: Date) {
    const params = {
      from: time.toISOString(),
      shiftId,
    };
    return firstValueFrom(
      this.http
        .get(
          `${environment.services.service}v1/vehicle/${vehicleId}/dashcam-image`,
          {
            params,
            responseType: 'blob',
          },
        )
        .pipe(
          retry(0),
          catchError(HttpErrorHandler.handleError) // then handle the error
        )
    );
  }

  getLastShiftImage(shiftId: number) {
    return firstValueFrom(
      this.http
        .get<JsonApiResponse<ImageModel>>(
          `${environment.services.service}v1/image/shift/${shiftId}/last`
        )
        .pipe(
          catchError(HttpErrorHandler.handleError), // then handle the error
          map(response => response.data),
        )
    );
  }

  getImagesPage(from: Moment,
                to: Moment,
                vehicleGroupFilter: MultiSelectFilter<number>,
                page: number = 1, size: number = 25, sort?: string) {
    const params = {
      page: String(page),
      size: String(size),
      sort
    };

    if (!!from) {
      params['validFrom'] = from.toISOString();
    }

    if (!!to) {
      params['validTo'] = to.toISOString();
    }

    if (!!vehicleGroupFilter && !!vehicleGroupFilter.elements) {
      params['vehicleGroupIds'] = vehicleGroupFilter.elements.join(':::');
    }

    return firstValueFrom(
      this.http
        .get<JsonApiResponse<Page<ImageWithDriverAndVehicle>>>(
          `${environment.services.service}v1/image/pagedList`, { params }
        )
        .pipe(
          catchError(HttpErrorHandler.handleError) // then handle the error
        )
    );
  }

  getCadenceImagesPerShift(shiftId: number) {
    return firstValueFrom(
      this.http
        .get<JsonApiResponse<ImageModel[]>>(
          `${environment.services.service}v1/images/shift/${shiftId}`
        )
        .pipe(
          catchError(HttpErrorHandler.handleError) // then handle the error
        )
    );
  }

  getImages(maxAge: Date = null)  {
    const url = `${environment.services.service}v1/images/list`;
    let params = new HttpParams();
    if (maxAge) {
      params = params.set('maxAge', maxAge.toISOString());
    }
    // observation images excluded
    return firstValueFrom(
      this.http
        .get<JsonApiResponse<ImageModel[]>>(url, {params})
        .pipe(
          retry(3), // retry a failed request up to 3 times
          catchError(HttpErrorHandler.handleError) // then handle the error
        )
    );
  }

  getVideoStreamURL(vehicleId: number, timeFrom: Date, timeTo: Date) {
    let params = new HttpParams();
    if (!!timeFrom) {
      params = params.set('from', timeFrom.toISOString());
    }
    if (!!timeTo) {
      params = params.set('to', timeTo.toISOString());
    }
    return firstValueFrom(
      this.http
        .get<JsonApiResponse<string>>(
          `${environment.services.service}v1/vehicle/${vehicleId}/hls`, {params}
        )
        .pipe(
            catchError(HttpErrorHandler.handleError) // then handle the error
        )
    );
  }
}
